/**
 * @description
 * Report scheduler component for creating and managing scheduled reports.
 * Provides UI for configuring report frequency, recipients, and content options.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/components/vscode-lens/ReportScheduler.test.tsx
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Play, Calendar, Mail, FileText, Clock } from 'lucide-react';
import { api } from '../../services/api';
import type { ReportSchedule, VSCodeLensTable } from '../../../types/vscode-lens';

interface ReportSchedulerProps {
  /** Available tables to report on */
  tables: VSCodeLensTable[];
  
  /** Callback when schedule is created/updated */
  onScheduleChange?: () => void;
}

/**
 * Report scheduler management component
 */
export function ReportScheduler({ tables, onScheduleChange }: ReportSchedulerProps) {
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ReportSchedule | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    enabled: true,
    frequency: 'daily' as 'daily' | 'weekly',
    dayOfWeek: 1,
    timeOfDay: '09:00',
    table: tables[0] || 'commit_events' as VSCodeLensTable,
    recipients: '',
    formats: ['pdf'] as ('pdf' | 'csv')[],
    includeCharts: true,
    includeTimeSeries: true,
    includeTrends: true,
  });

  useEffect(() => {
    loadSchedules();
  }, []);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const data = await api.getReportSchedules();
      setSchedules(data);
    } catch (error) {
      console.error('Error loading schedules:', error);
      toast.error('Failed to load report schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate recipients
    const recipientList = formData.recipients
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (recipientList.length === 0) {
      toast.error('Please enter at least one recipient email');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = recipientList.filter(email => !emailRegex.test(email));
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email addresses: ${invalidEmails.join(', ')}`);
      return;
    }

    try {
      const scheduleData = {
        ...formData,
        recipients: recipientList,
        dayOfWeek: formData.frequency === 'weekly' ? formData.dayOfWeek : undefined,
      };

      if (editingSchedule) {
        await api.updateReportSchedule(editingSchedule.id, scheduleData);
        toast.success('Schedule updated successfully');
      } else {
        await api.createReportSchedule(scheduleData);
        toast.success('Schedule created successfully');
      }

      setShowForm(false);
      setEditingSchedule(null);
      resetForm();
      await loadSchedules();
      onScheduleChange?.();
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule');
    }
  };

  const handleEdit = (schedule: ReportSchedule) => {
    setEditingSchedule(schedule);
    setFormData({
      name: schedule.name,
      enabled: schedule.enabled,
      frequency: schedule.frequency,
      dayOfWeek: schedule.dayOfWeek || 1,
      timeOfDay: schedule.timeOfDay,
      table: schedule.table,
      recipients: schedule.recipients.join(', '),
      formats: schedule.formats,
      includeCharts: schedule.includeCharts,
      includeTimeSeries: schedule.includeTimeSeries,
      includeTrends: schedule.includeTrends,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      await api.deleteReportSchedule(id);
      toast.success('Schedule deleted successfully');
      await loadSchedules();
      onScheduleChange?.();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  const handleRunNow = async (id: string, name: string) => {
    try {
      await api.runReportSchedule(id);
      toast.success(`Report generation started for "${name}"`);
    } catch (error) {
      console.error('Error running schedule:', error);
      toast.error('Failed to run schedule');
    }
  };

  const handleToggleEnabled = async (schedule: ReportSchedule) => {
    try {
      await api.updateReportSchedule(schedule.id, { enabled: !schedule.enabled });
      toast.success(`Schedule ${!schedule.enabled ? 'enabled' : 'disabled'}`);
      await loadSchedules();
      onScheduleChange?.();
    } catch (error) {
      console.error('Error toggling schedule:', error);
      toast.error('Failed to update schedule');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      enabled: true,
      frequency: 'daily',
      dayOfWeek: 1,
      timeOfDay: '09:00',
      table: tables[0] || 'commit_events',
      recipients: '',
      formats: ['pdf'],
      includeCharts: true,
      includeTimeSeries: true,
      includeTrends: true,
    });
  };

  const formatNextRun = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading schedules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Report Schedules</h2>
          <p className="text-sm text-muted-foreground">
            Automate report generation and delivery
          </p>
        </div>
        <button
          onClick={() => {
            setEditingSchedule(null);
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Schedule
        </button>
      </div>

      {/* Schedule Form */}
      {showForm && (
        <div className="rounded-lg border bg-card p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Schedule Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Weekly Commit Report"
                  required
                />
              </div>

              {/* Table */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Table *
                </label>
                <select
                  value={formData.table}
                  onChange={(e) => setFormData({ ...formData, table: e.target.value as VSCodeLensTable })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                >
                  {tables.map(table => (
                    <option key={table} value={table}>{table}</option>
                  ))}
                </select>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Frequency *
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as 'daily' | 'weekly' })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>

              {/* Day of Week (for weekly) */}
              {formData.frequency === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Day of Week *
                  </label>
                  <select
                    value={formData.dayOfWeek}
                    onChange={(e) => setFormData({ ...formData, dayOfWeek: parseInt(e.target.value) })}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                  >
                    <option value={0}>Sunday</option>
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                  </select>
                </div>
              )}

              {/* Time of Day */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Time of Day *
                </label>
                <input
                  type="time"
                  value={formData.timeOfDay}
                  onChange={(e) => setFormData({ ...formData, timeOfDay: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                />
              </div>
            </div>

            {/* Recipients */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Recipients * (comma-separated emails)
              </label>
              <input
                type="text"
                value={formData.recipients}
                onChange={(e) => setFormData({ ...formData, recipients: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="user1@example.com, user2@example.com"
                required
              />
            </div>

            {/* Formats */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Report Formats *
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.formats.includes('pdf')}
                    onChange={(e) => {
                      const formats = e.target.checked
                        ? [...formData.formats, 'pdf']
                        : formData.formats.filter(f => f !== 'pdf');
                      setFormData({ ...formData, formats: formats as ('pdf' | 'csv')[] });
                    }}
                    className="rounded border-input"
                  />
                  PDF
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.formats.includes('csv')}
                    onChange={(e) => {
                      const formats = e.target.checked
                        ? [...formData.formats, 'csv']
                        : formData.formats.filter(f => f !== 'csv');
                      setFormData({ ...formData, formats: formats as ('pdf' | 'csv')[] });
                    }}
                    className="rounded border-input"
                  />
                  CSV
                </label>
              </div>
            </div>

            {/* Content Options */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Report Content
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.includeCharts}
                    onChange={(e) => setFormData({ ...formData, includeCharts: e.target.checked })}
                    className="rounded border-input"
                  />
                  Include Charts
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.includeTimeSeries}
                    onChange={(e) => setFormData({ ...formData, includeTimeSeries: e.target.checked })}
                    className="rounded border-input"
                  />
                  Include Time-Series Analysis
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.includeTrends}
                    onChange={(e) => setFormData({ ...formData, includeTrends: e.target.checked })}
                    className="rounded border-input"
                  />
                  Include Trend Analysis
                </label>
              </div>
            </div>

            {/* Enabled Toggle */}
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="rounded border-input"
                />
                <span className="text-sm font-medium">Enable schedule immediately</span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingSchedule(null);
                  resetForm();
                }}
                className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Schedules List */}
      <div className="space-y-4">
        {schedules.length === 0 ? (
          <div className="rounded-lg border bg-card p-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-semibold">No Schedules Yet</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Create your first report schedule to automate report generation
            </p>
          </div>
        ) : (
          schedules.map(schedule => (
            <div
              key={schedule.id}
              className="rounded-lg border bg-card p-4 hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold truncate">{schedule.name}</h3>
                    <span
                      className={`px-2 py-1 rounded-md text-xs font-medium ${
                        schedule.enabled
                          ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {schedule.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>

                  <div className="grid gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span>{schedule.table}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>
                        {schedule.frequency === 'daily' ? 'Daily' : `Weekly on ${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][schedule.dayOfWeek || 0]}`} at {schedule.timeOfDay}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <span>{schedule.recipients.length} recipient{schedule.recipients.length !== 1 ? 's' : ''}</span>
                    </div>
                    {schedule.nextRun && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>Next run: {formatNextRun(schedule.nextRun)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRunNow(schedule.id, schedule.name)}
                    className="p-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                    title="Run Now"
                  >
                    <Play className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(schedule)}
                    className="p-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleToggleEnabled(schedule)}
                    className="p-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                    title={schedule.enabled ? 'Disable' : 'Enable'}
                  >
                    <Clock className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(schedule.id)}
                    className="p-2 rounded-md border border-input bg-background hover:bg-destructive hover:text-destructive-foreground transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */